/**
 * Date helpers for the grid.
 *
 * The APIs behind these grids emit dates as `DD-MM-YYYY` strings (`TO_CHAR(col,
 * 'DD-MM-YYYY')` is the house convention across the ds-wms services). `new Date()`
 * reads that shape as MM-DD-YYYY, so `18-08-2026` rendered as "Invalid Date" and
 * `05-08-2026` silently rendered as 8 May instead of 5 August. Parse the day-first
 * form explicitly before falling back to the engine's parser.
 */
export const parseGridDate = (value: unknown): Date | null => {
  if (value == null || value === '') return null;

  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof value !== 'string') return null;

  const raw = value.trim();
  if (!raw) return null;

  // DD-MM-YYYY / DD/MM/YYYY / DD.MM.YYYY (optionally followed by a time)
  const dayFirst = raw.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (dayFirst) {
    const [, dd, mm, yyyy, hh = '0', mi = '0', ss = '0'] = dayFirst;
    const day = Number(dd);
    const month = Number(mm);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const d = new Date(Number(yyyy), month - 1, day, Number(hh), Number(mi), Number(ss));
    // Reject roll-overs such as 31-02-2026
    if (d.getFullYear() !== Number(yyyy) || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
    return d;
  }

  const parsed = new Date(raw);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/** DD/MM/YYYY — the format these users read dates in. */
export const formatGridDate = (value: unknown): string | null => {
  const d = parseGridDate(value);
  if (!d) return null;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
